/**
 * Centralized step paths configuration for the financial application flow.
 * This provides a hierarchical structure for managing step paths throughout the application.
 */

// Main step paths
export const STEP_PATHS = {
  SelectProduct: "select-product",
  Verification: "verification",
  AboutYou: "about-you",
  JointOwners: "joint-owners",
  Beneficiaries: "beneficiaries",
  EntityInfo: "entity-info",
  AdditionalInfo: "additional-info",
  ConfirmDetails: "confirm-details",
} as const;

// Substeps IDs (used in HomePage.tsx for step configuration)
export const SUBSTEP_IDS = {
  // About You substeps
  AboutYou: {
    AccountSignup: "account-signup",
    PreparingYourApplication: "preparing-your-application",
    IdentityVerification: "identity-verification",
    BasicInfo: "basic-info",
    AccountDetails: "account-details",
    ContactInfo: "contact-info",
    EmploymentInfo: "employment-info",
  },

  // Entity Info substeps
  EntityInfo: {
    EntityBasicInfo: "entity-basic-info",
    KeyPeople: "key-people",
  },

  // Additional Info substeps
  AdditionalInfo: {
    PersonalAccountAcceptableUse: "personal-account-acceptable-use",
    OrganizationAcceptableUse: "organization-acceptable-use",
    DigitalBankingInfo: "digital-banking-info",
    DebitCardInfo: "debit-card-info",
    CreditAuthorization: "credit-authorization",
    Preferences: "preferences",
    ApplicationDocuments: "application-documents",
  },

  // Verification substeps (dynamic based on disclosures)
  Verification: {
    // These are dynamically generated based on disclosure slugs
    // Format: `disclosure-${disclosure.slug}`
    getDisclosureId: (disclosureSlug: string) => `disclosure-${disclosureSlug}`,
  },
} as const;

// Substeps paths that reference main step paths (used for navigation)
export const SUBSTEP_PATHS = {
  // About You substeps
  AboutYou: {
    AccountSignup: `${STEP_PATHS.AboutYou}/${SUBSTEP_IDS.AboutYou.AccountSignup}`,
    PreparingYourApplication: `${STEP_PATHS.AboutYou}/${SUBSTEP_IDS.AboutYou.PreparingYourApplication}`,
    IdentityVerification: `${STEP_PATHS.AboutYou}/${SUBSTEP_IDS.AboutYou.IdentityVerification}`,
    BasicInfo: `${STEP_PATHS.AboutYou}/${SUBSTEP_IDS.AboutYou.BasicInfo}`,
    AccountDetails: `${STEP_PATHS.AboutYou}/${SUBSTEP_IDS.AboutYou.AccountDetails}`,
    ContactInfo: `${STEP_PATHS.AboutYou}/${SUBSTEP_IDS.AboutYou.ContactInfo}`,
    EmploymentInfo: `${STEP_PATHS.AboutYou}/${SUBSTEP_IDS.AboutYou.EmploymentInfo}`,
  },

  // Entity Info substeps
  EntityInfo: {
    EntityBasicInfo: `${STEP_PATHS.EntityInfo}/${SUBSTEP_IDS.EntityInfo.EntityBasicInfo}`,
    KeyPeople: `${STEP_PATHS.EntityInfo}/${SUBSTEP_IDS.EntityInfo.KeyPeople}`,
  },

  // Additional Info substeps
  AdditionalInfo: {
    PersonalAccountAcceptableUse: `${STEP_PATHS.AdditionalInfo}/${SUBSTEP_IDS.AdditionalInfo.PersonalAccountAcceptableUse}`,
    OrganizationAcceptableUse: `${STEP_PATHS.AdditionalInfo}/${SUBSTEP_IDS.AdditionalInfo.OrganizationAcceptableUse}`,
    DigitalBankingInfo: `${STEP_PATHS.AdditionalInfo}/${SUBSTEP_IDS.AdditionalInfo.DigitalBankingInfo}`,
    DebitCardInfo: `${STEP_PATHS.AdditionalInfo}/${SUBSTEP_IDS.AdditionalInfo.DebitCardInfo}`,
    CreditAuthorization: `${STEP_PATHS.AdditionalInfo}/${SUBSTEP_IDS.AdditionalInfo.CreditAuthorization}`,
    Preferences: `${STEP_PATHS.AdditionalInfo}/${SUBSTEP_IDS.AdditionalInfo.Preferences}`,
    ApplicationDocuments: `${STEP_PATHS.AdditionalInfo}/${SUBSTEP_IDS.AdditionalInfo.ApplicationDocuments}`,
  },

  // Verification substeps (dynamic based on disclosures)
  Verification: {
    // These are dynamically generated based on disclosure slugs
    // Format: `${STEP_PATHS.Verification}/disclosure-${disclosure.slug}`
    getDisclosurePath: (disclosureSlug: string) =>
      `${STEP_PATHS.Verification}/disclosure-${disclosureSlug}`,
  },
} as const;

/**
 * Type definitions for step paths to ensure type safety
 */

export type StepPathKey = keyof typeof STEP_PATHS;
export type AboutYouStepIdKey = keyof typeof SUBSTEP_IDS.AboutYou;
export type EntityInfoStepIdKey = keyof typeof SUBSTEP_IDS.EntityInfo;
export type AdditionalInfoStepIdKey = keyof typeof SUBSTEP_IDS.AdditionalInfo;

/**
 * Utility functions for working with step paths
 */
export const StepPaths = {
  /**
   * Get a main step path by key
   */
  get: (key: StepPathKey): string => {
    return STEP_PATHS[key];
  },

  /**
   * Get an About You substep ID
   */
  getAboutYouStepId: (key: AboutYouStepIdKey): string => {
    return SUBSTEP_IDS.AboutYou[key];
  },

  /**
   * Get an Entity Info substep ID
   */
  getEntityInfoStepId: (key: EntityInfoStepIdKey): string => {
    return SUBSTEP_IDS.EntityInfo[key];
  },

  /**
   * Get an Additional Info substep ID
   */
  getAdditionalInfoStepId: (key: AdditionalInfoStepIdKey): string => {
    return SUBSTEP_IDS.AdditionalInfo[key];
  },

  /**
   * Get an About You substep path
   */
  getAboutYouStepPath: (key: AboutYouStepIdKey): string => {
    return SUBSTEP_PATHS.AboutYou[key];
  },

  /**
   * Get an Entity Info substep path
   */
  getEntityInfoStepPath: (key: EntityInfoStepIdKey): string => {
    return SUBSTEP_PATHS.EntityInfo[key];
  },

  /**
   * Get an Additional Info substep path
   */
  getAdditionalInfoStepPath: (key: AdditionalInfoStepIdKey): string => {
    return SUBSTEP_PATHS.AdditionalInfo[key];
  },

  /**
   * Get a verification disclosure ID
   */
  getVerificationDisclosureId: (disclosureSlug: string): string => {
    return SUBSTEP_IDS.Verification.getDisclosureId(disclosureSlug);
  },

  /**
   * Get a verification disclosure path
   */
  getVerificationDisclosurePath: (disclosureSlug: string): string => {
    return SUBSTEP_PATHS.Verification.getDisclosurePath(disclosureSlug);
  },

  /**
   * Build a full step URL with optional substep
   */
  buildStepUrl: (stepId: string, substepId?: string): string => {
    return substepId ? `/step/${stepId}/${substepId}` : `/step/${stepId}`;
  },

  /**
   * Check if a path is a main step (not a substep)
   */
  isMainStep: (path: string): boolean => {
    return Object.values(STEP_PATHS).includes(path as any);
  },

  /**
   * Get the parent step for a given substep path
   */
  getParentStep: (path: string): string | null => {
    if (path.startsWith(STEP_PATHS.AboutYou + "/")) {
      return STEP_PATHS.AboutYou;
    }
    if (path.startsWith(STEP_PATHS.EntityInfo + "/")) {
      return STEP_PATHS.EntityInfo;
    }
    if (path.startsWith(STEP_PATHS.AdditionalInfo + "/")) {
      return STEP_PATHS.AdditionalInfo;
    }
    if (path.startsWith(STEP_PATHS.Verification + "/")) {
      return STEP_PATHS.Verification;
    }
    return null;
  },
};
